import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import CallexLoader from '../../../../components/CallexLoader';
import Pagination from '../../../../components/form/Pagination';
import AdminHeader from '../../../../components/header/AdminHeader';
import DeleteConfirmationModal from '../../../../components/Modals/DeleteConfirmationModal';
import AddEditBeatModal from './AddEditBeatModal';
import ImportBeatModal from './ImportBeatModal';

import {
  beatDelete,
  beatListGet,
  useBeatList
} from '../../../../store/slices/tenantSlices/productSlice';
import { checkModulePermission } from '../../../../utils/common';
import { useUserRolePermission } from '../../../../store/slices/authSlice';

const Beat = () => {
  const dispatch = useDispatch();
  const beatList = useBeatList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchBeatList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(beatListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteBeat = async () => {
    try {
      const response = await dispatch(beatDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (beatList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchBeatList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchBeatList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Beat"
        isExpandSearch
        isAdd
        onAddClick={() => setOpenAddModal(true)}
        isImport
        isImportDisabled={!checkModulePermission('is_import', 'Beat', userRolePermission)}
        isAddDisabled={!checkModulePermission('is_add', 'Beat', userRolePermission)}
        onImportClick={() => setOpenImportModal(true)}
        addBtnName="Add Beat"
        searchPlaceholder="Search Beat"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : beatList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Beat', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Area Name</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {beatList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.name}</td>
                      <td>{val?.area_name}</td>
                      <td>{String(val?.status)}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Beat', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (checkModulePermission('is_edit', 'Beat', userRolePermission)) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', 'Beat', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_delete', 'Beat', userRolePermission)
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={beatList?.total}
              limit={beatList?.limit}
              page={beatList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchBeatList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteBeat()}
      />
      <AddEditBeatModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchBeatList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
      <ImportBeatModal
        isOpen={openImportModal}
        onCloseClick={(apiCall = false) => {
          setOpenImportModal(false);
          if (apiCall) {
            fetchBeatList();
          }
        }}
      />
    </div>
  );
};

export default Beat;
